"use client";
import { createImageUpload } from "novel";
import { toast } from "sonner";
import type { StorageAdapter } from "@/lib/storage/adapter";

export function makeUploadFn(adapter: StorageAdapter) {
  const onUpload = async (file: File) => {
    // Try remote upload first
    try {
      const res = await fetch("/api/upload", {
        method: "POST",
        headers: {
          "content-type": file?.type || "application/octet-stream",
          "x-vercel-filename": file?.name || "image.png",
        },
        body: file,
      });
      if (res.status === 200) {
        const { url } = (await res.json()) as { url: string };
        return await new Promise((resolve) => {
          const img = new Image();
          img.src = url;
          img.onload = () => resolve(url);
        });
      }
      if (res.status !== 401) throw new Error("Upload failed");
    } catch {
      // ignore and fall back to local storage
    }

    // Local-first fallback: store in IndexedDB assets and return a ploom://asset/{blobKey}
    try {
      const { blobKey } = await adapter.putAsset(file);
      return `ploom://asset/${blobKey}`;
    } catch {}
    return file; // fallback
  };

  return createImageUpload({
    onUpload: (file: File) => {
      const p = onUpload(file);
      // Show toast bound to the promise; return the original promise
      // so the plugin awaits the final value (url | File)
      // @ts-ignore sonner typings return a ToastT rather than Promise
      toast.promise(p as any, {
        loading: "Uploading image...",
        success: "Image added.",
        error: (e) => (e as Error).message || "Upload failed",
      });
      return p;
    },
    validateFn: (file) => {
      if (!file.type.includes("image/")) {
        toast.error("File type not supported.");
        return false;
      }
      if (file.size / 1024 / 1024 > 20) {
        toast.error("File size too big (max 20MB).");
        return false;
      }
      return true;
    },
  });
}
